import React, { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface LockoutNoticeProps {
  message: string;
  lockedUntil: Date;
  onExpire: () => void;
}

const formatRemaining = (seconds: number): string => {
  const safe = Math.max(0, Math.ceil(seconds));
  const minutes = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const remainingSeconds = (until: Date): number =>
  (until.getTime() - Date.now()) / 1000;

const LockoutNotice: React.FC<LockoutNoticeProps> = ({
  message,
  lockedUntil,
  onExpire,
}) => {
  const [remaining, setRemaining] = useState(() =>
    remainingSeconds(lockedUntil)
  );

  useEffect(() => {
    setRemaining(remainingSeconds(lockedUntil));

    const interval = window.setInterval(() => {
      const next = remainingSeconds(lockedUntil);
      setRemaining(next);
      if (next <= 0) {
        window.clearInterval(interval);
        onExpire();
      }
    }, 1000);

    return () => window.clearInterval(interval);
  }, [lockedUntil, onExpire]);

  if (remaining <= 0) return null;

  return (
    <Alert variant="destructive" data-testid="lockout-notice">
      <AlertDescription>
        <span>{message}</span>{' '}
        <span data-testid="lockout-countdown">
          Try again in {formatRemaining(remaining)}.
        </span>
      </AlertDescription>
    </Alert>
  );
};

export default LockoutNotice;
